import { TRADE, WHARVES, GOODS } from '../config.js';

// ---------------------------------------------------------------------------
// The goods trade.
//
// Buy at a mill wharf on the Zaanse Schans, carry it east, sell it at
// Volendam. The same hold as the herring and the passengers, so it is the same
// one-cargo choice — and the first of the three that can lose you money, since
// you pay for the cargo up front and the price can move while you sail.
//
// Both ends move: the mill's price climbs as its stock goes down, Volendam's
// falls as its stalls fill. Work one good too hard and the spread closes from
// both sides, which is the game telling you to carry something else.
//
// Shut at night. The millers are asleep and the stalls are shuttered — you can
// still be carrying, you just cannot buy or sell until morning.
// ---------------------------------------------------------------------------

export class Trading {
    constructor(sailing, hud, cargo, fleet) {
        this.sailing = sailing;
        this.hud = hud;
        this.cargo = cargo;      // the Fishing instance: same hold, same purse
        this.fleet = fleet;
        this.good = null;        // what is aboard
        this.count = 0;
        this.night = 0;
        this.traded = false;     // true for one frame, for the card to kick

        // Prices, until the relay says otherwise. Offline you still get a
        // market, it just does not move for anyone else.
        this.market = {};
        for (const g of GOODS) this.market[g.id] = { buy: 4, sell: 5.4, stock: 40 };
    }

    get closed() {
        return this.night > 0.45;
    }

    /** The wharf the boat is lying at, if any. */
    wharfAt() {
        const { x, z } = this.sailing.position;
        for (const w of WHARVES) {
            const b = w.box;
            if (x >= b.x0 && x <= b.x1 && z >= b.z0 && z <= b.z1) return w;
        }
        return null;
    }

    /** How many the next press would take. Zero means it would do nothing. */
    lotHere() {
        const wharf = this.wharfAt();
        if (!wharf || this.closed) return 0;
        // One cargo at a time, and it is the whole hold.
        if (this.cargo.hold > 0 || this.cargo.crossing?.passengers > 0) return 0;
        if (this.good && this.good !== wharf.id) return 0;

        const price = this.market[wharf.id]?.buy ?? 0;
        if (price <= 0) return 0;
        return Math.max(0, Math.min(
            TRADE.lot,
            TRADE.capacity - this.count,
            Math.floor(this.market[wharf.id]?.stock ?? 0),
            Math.floor(this.cargo.guilders / price),
        ));
    }

    get canBuy() {
        return this.lotHere() > 0;
    }

    get canSell() {
        if (!this.count || this.closed) return false;
        // Volendam's market, the same water the fish are sold in.
        return this.cargo.crossing?.portAt()?.id === 'volendam';
    }

    buy() {
        const wharf = this.wharfAt();
        const take = this.lotHere();
        if (!wharf || take <= 0) return;

        const price = this.market[wharf.id].buy;
        this.cargo.guilders -= Math.round(take * price);
        this.market[wharf.id].stock -= take;
        this.good = wharf.id;
        this.count += take;
        this.traded = true;
        this.cargo._save();
        this.fleet?.buy(wharf.id, take);
    }

    sell() {
        if (!this.canSell) return;
        const price = this.market[this.good]?.sell ?? 0;
        this.cargo.guilders += Math.round(this.count * price);
        this.fleet?.unload(this.good, this.count);
        this.good = null;
        this.count = 0;
        this.traded = true;
        this.cargo._save();
    }

    /**
     * The relay had the last word on how many the pile held. Ours was a guess
     * made to keep the press responsive; this is the truth.
     */
    confirmBought(good, count, price) {
        if (!count) return;
        this.good = good;
        this.count = count;
        this.traded = true;
    }

    /** The relay's prices, which outrank ours the moment it has any. */
    setMarket(market) {
        if (market) Object.assign(this.market, market);
    }

    update(dt, night = 0) {
        this.night = night;
        this.traded = false;
    }

    /** What this good is called, for the hints and the card. */
    nameOf(id) {
        return GOODS.find((g) => g.id === id)?.name ?? id;
    }
}
